import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import styled from 'styled-components';
import {FlexColumn, FlexRow, Modal, SearchField, Table, Text, useTableFunctions} from '@forecast-it/design-system';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import Util, {MAX_FILES_PER_UPLOAD} from '../../forecast-app/shared/util/util';
import AttachProjectFileToTaskMutation from '../../mutations/attach_project_file_to_task_mutation';
// Same helper Util.uploadFiles uses for its partial-failure summary, so a partial attach failure
// reads the way a partial upload failure already does.
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';

type FolderRef = {id: string} | null;
type ProjectFile = {id: string; name: string; size: number; mimeType: string; folder: FolderRef};
type ProjectFolder = {id: string; name: string; folder: FolderRef};
type TaskFile = {id: string};

// The attach mutation reports a business-rule rejection in its payload rather than throwing, so
// onSuccess has to inspect it. Only the two fields this flow reads are declared.
type AttachMutationResponse = {
	attachProjectFileToTask?: {error?: string | null; filename?: string | null} | null;
} | null;

type AttachProjectFilesRelayProps = {
	viewer?: {
		project?: {
			files: {edges: {node: ProjectFile}[]};
			folders: {edges: {node: ProjectFolder}[]};
		} | null;
		task?: {
			referencedFiles: {edges: {node: TaskFile}[]};
		} | null;
	} | null;
} | null;

export type PickerRow = {
	id: string;
	name: string;
	isFolder: boolean;
	alreadyAttached: boolean;
	size?: number;
	mimeType?: string;
};

// The design-system Text takes no onClick -- TextProps has none -- so the click target is a real
// button reset to look like inline text. That is also the accessible choice for a navigation
// control: it is focusable and keyboard-operable, which a clickable span would not be.
const BreadcrumbCrumb = styled.button`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	font: inherit;
	color: inherit;
	cursor: pointer;
`;

const folderIdOf = (item: {folder: FolderRef}): string | null => (item.folder ? item.folder.id : null);

/**
 * The one definition of "this row can be attached". Folders are listed so the user can descend but
 * can never be attached, and a file already linked to this task cannot be attached twice.
 *
 * Every site that needs the rule reads it from here — the Table's `enableRowSelection`, the
 * selection merge, and the count the footer shows — so relaxing it (say, allowing a re-attach) is a
 * one-line change and cannot leave the picker showing a row as ticked that nothing will count.
 */
export function isRowSelectable(row: PickerRow): boolean {
	return !row.isFolder && !row.alreadyAttached;
}

/**
 * Rows for one level of the folder tree. Mirrors ProjectFilesPage.setElements: search is applied
 * first, then the level filter, so a search never reaches into another folder. Folders are returned
 * as rows so the user can descend, but always carry isFolder so nothing can select them — a folder
 * cannot be attached to a task.
 *
 * `selectedIds` keeps a ticked file visible even when the search term does not match its name. A row
 * that is selected but filtered out of view cannot be unticked and is not covered by the level's
 * clear, so it would keep counting towards the footer total with nothing on screen to explain it —
 * and would still be attached. Selection at the open level is therefore always visible; only other
 * folders hold ticks the user cannot see, and the breadcrumb explains those.
 */
export function pickerRowsForLevel(
	projectFiles: ProjectFile[],
	projectFolders: ProjectFolder[],
	openFolderId: string | null,
	searchValue: string,
	taskFiles: TaskFile[],
	// Membership is the only thing needed, so a Set and the selection Map both satisfy this — the
	// caller passes its Map straight in rather than rebuilding a Set on every render.
	selectedIds: {has: (id: string) => boolean} = new Set<string>()
): PickerRow[] {
	const attachedIds = new Set(taskFiles.map(f => f.id));
	const matches = (name: string) => !searchValue || name.toLowerCase().includes(searchValue.toLowerCase());

	const folderRows: PickerRow[] = projectFolders
		.filter(folder => folderIdOf(folder) === openFolderId && matches(folder.name))
		.map(folder => ({id: folder.id, name: folder.name, isFolder: true, alreadyAttached: false}));

	const fileRows: PickerRow[] = projectFiles
		.filter(file => folderIdOf(file) === openFolderId && (matches(file.name) || selectedIds.has(file.id)))
		.map(file => ({
			id: file.id,
			name: file.name,
			isFolder: false,
			// Matched on file id: a link points at the project's own file row, so the id is the
			// identity on both sides. There is no second row and no shared key to compare.
			alreadyAttached: attachedIds.has(file.id),
			size: file.size,
			mimeType: file.mimeType,
		}));

	return [...folderRows, ...fileRows];
}

/**
 * Merges the Table's report of what is ticked at the open level into the whole-picker selection.
 *
 * `levelRows` must be every row the open level currently shows. All of them are dropped first, so
 * anything the Table did not report back is deselected; ticks recorded for other folders are left
 * untouched, which is what lets a selection survive folder navigation.
 *
 * The insert side is the authority on what may be selected: `isRowSelectable` is applied here rather
 * than trusted from the Table, so no unattachable row can enter the selection whatever the Table
 * reports. The clear side is deliberately unconditional — it drops folders too — so that the map can
 * always be emptied by deselecting, independently of how an entry got in.
 *
 * Returns a new Map; `previous` is not modified. Callers should pass it through the `setState`
 * updater so the merge always reads the committed selection.
 *
 * Pure and exported because the design-system Table renders through @tanstack/react-virtual, which
 * emits zero rows under JSDOM: no test can reach a real checkbox, so the merge has to be assertable
 * on its own.
 */
export function reconcileSelection(
	previous: Map<string, PickerRow>,
	levelRows: PickerRow[],
	levelSelection: PickerRow[] | null | undefined
): Map<string, PickerRow> {
	const next = new Map(previous);
	(levelRows || []).forEach(row => next.delete(row.id));
	(levelSelection || []).filter(row => row && isRowSelectable(row)).forEach(row => next.set(row.id, row));
	return next;
}

/**
 * The footer button's only guard against firing one mutation per selected file without bound.
 * Exported so the boundary is assertable directly — reading the constant back in a test proves
 * nothing about whether this rule is applied.
 */
export function isAttachBlocked(selectedCount: number): boolean {
	return selectedCount === 0 || selectedCount > MAX_FILES_PER_UPLOAD;
}

/**
 * Mirrors the STRUCTURE of the summary Util.uploadFiles builds, with its own message ids.
 *
 * Deliberately does NOT reuse file_upload.uploaded-file / file_upload.rejected-files. They read
 * "The following files were uploaded:" and "The following files are not allowed to be uploaded:".
 * Nothing is uploaded in this flow — that is the entire point of the feature — so the success toast
 * would be actively misleading and the failure toast wrong about the cause. The structural
 * mirroring is right; the words are not.
 */
export function buildAttachSummary(attached: string[], failed: string[]) {
	return (
		<div>
			{attached.length > 0 ? <FormattedHTMLMessage id="task_modal.attach_project_file_attached" /> : null}
			{attached.length > 0 ? <br /> : null}
			{attached.length > 0 ? attached.join(', ') : null}
			{attached.length > 0 ? <br /> : null}
			{failed.length > 0 ? <FormattedHTMLMessage id="task_modal.attach_project_file_failed" /> : null}
			{failed.length > 0 ? <br /> : null}
			{failed.length > 0 ? failed.join(', ') : null}
		</div>
	);
}

export const AttachProjectFilesModalContent = ({
	projectFiles,
	projectFolders,
	taskFiles,
	taskId,
	closeModal,
}: {
	projectFiles: ProjectFile[];
	projectFolders: ProjectFolder[];
	taskFiles: TaskFile[];
	taskId: string;
	closeModal: () => void;
}) => {
	const {formatMessage} = useIntl();
	const [searchValue, setSearchValue] = useState('');
	// The trail of folders the user has descended through. Its last element is the open folder; empty
	// means the project's top level. Doubles as the breadcrumb, so there is one source of truth for
	// "where am I" — ProjectFilesPage keeps these separate (selectedFolder plus pathFolders) and has
	// to reconcile them.
	const [trail, setTrail] = useState<ProjectFolder[]>([]);
	// Selection is keyed by file id and lives above the level state, so descending into a folder
	// keeps what is already ticked and the cap applies to the whole selection, not per folder.
	const [selectedById, setSelectedById] = useState<Map<string, PickerRow>>(new Map());
	// setSelections replaces the Table's whole selection map rather than merging into it, which is what
	// lets a single call scope that map to the open level. The hook's ref starts out stubbed with
	// no-ops, so calling through `table` before the Table has mounted is safe.
	const [tableRef, table] = useTableFunctions();

	const openFolderId = trail.length ? trail[trail.length - 1].id : null;

	const rows = useMemo(
		() => pickerRowsForLevel(projectFiles, projectFolders, openFolderId, searchValue, taskFiles, selectedById),
		[projectFiles, projectFolders, openFolderId, searchValue, taskFiles, selectedById]
	);

	// The merge runs from a deferred callback (see onSelectionChange), by which point the render that
	// produced `rows` may have been replaced. Reading the level through a ref means the clear step
	// always applies to the level on screen when the callback runs, not the one that scheduled it.
	const rowsRef = useRef(rows);
	rowsRef.current = rows;

	// Selection is validated on read, not only on insert. An attach committed elsewhere writes into
	// Task_referencedFiles under an open modal, so a row captured as attachable can become attached
	// while it sits in the map. Re-deriving the effective selection from the current task files means
	// the count and the attach list both drop it instead of firing a duplicate attach.
	const attachedIds = useMemo(() => new Set(taskFiles.map(file => file.id)), [taskFiles]);
	const selectedRows = useMemo(
		() => Array.from(selectedById.values()).filter(row => isRowSelectable(row) && !attachedIds.has(row.id)),
		[selectedById, attachedIds]
	);
	const attachBlocked = isAttachBlocked(selectedRows.length);

	const openFolder = (row: PickerRow) => {
		const folder = projectFolders.find(f => f.id === row.id);
		if (folder) {
			setTrail([...trail, folder]);
			setSearchValue('');
		}
	};

	// index -1 returns to the project's top level.
	const goToTrailIndex = (index: number) => {
		setTrail(trail.slice(0, index + 1));
		setSearchValue('');
	};

	// Table dispatches this callback from inside a setTimeout, so by the time it runs both of its
	// inputs may be a render behind. The updater form takes care of the selection map and `rowsRef`
	// takes care of the level; neither is read from this closure.
	const onSelectionChange = (levelSelection: PickerRow[]) => {
		setSelectedById(previous => reconcileSelection(previous, rowsRef.current, levelSelection));
	};

	// Table owns a row-selection map of its own and never clears it when `data` changes, while its
	// header checkbox is driven by getIsSomeRowsSelected() — a count over EVERY key in that map,
	// including ids for rows the open level does not contain. Left alone, ticking a file in one folder
	// renders the header indeterminate everywhere else, and Radix maps indeterminate to checked, so
	// clicking it selects the level instead of clearing it. Narrowing the Table's map to the ids this
	// level actually shows keeps its header and the footer count describing the same thing.
	useEffect(() => {
		table.setSelections(rows.filter(row => selectedById.has(row.id)).map(row => row.id));
		// Level and search changes only. `rows` and `selectedById` are read for their current values;
		// listing them here would re-run this on every tick and overwrite the user's own clicks.
	}, [openFolderId, searchValue]);

	const attachSelected = () => {
		// Util.CommitMutation supplies the Relay environment and the retry wrapper; call it with the
		// mutation module rather than invoking commit() directly, matching every other call site. Its
		// signature is (mutationClass, input, onSuccess, retry, uploadables, onError), so `true` and
		// `null` must be passed positionally to reach onError.
		//
		// Both callbacks are load-bearing. The mutation reports a business-rule rejection as
		// payload.error rather than throwing, so with no onSuccess a source file deleted between
		// render and click fails SILENTLY: the modal closes and the user believes it worked.
		const attached: string[] = [];
		const failed: string[] = [];
		let settled = 0;
		const total = selectedRows.length;

		// One summary toast once every per-file mutation has settled, mirroring the pending-counter
		// pattern in Util.uploadFiles.
		const settle = () => {
			settled += 1;
			if (settled < total) return;
			createToast({duration: 5000, message: buildAttachSummary(attached, failed)});
		};

		selectedRows.forEach(row => {
			Util.CommitMutation(
				AttachProjectFileToTaskMutation,
				{taskId, sourceFileId: row.id},
				(response: AttachMutationResponse) => {
					// The payload carries the rejection rather than throwing.
					const payload = response && response.attachProjectFileToTask;
					if (payload && payload.error) {
						failed.push(payload.filename || row.name);
					} else {
						attached.push(row.name);
					}
					settle();
				},
				true,
				null,
				(transaction: unknown, onFailure: (t: unknown) => boolean | undefined) => {
					// CommitMutation hands a custom onError the fallback it displaced, and that fallback
					// is what retries a transient network failure and raises the correlation-id modal on
					// a server error. Skipping it reports a file as permanently failed that a retry would
					// have delivered, and discards the only identifier support can trace.
					//
					// A retry announces itself through the return value. The retried attempt settles the
					// counter, so this one must not, or the summary toast fires early and under-reports.
					const isRetrying = onFailure(transaction);
					if (isRetrying) return;

					failed.push(row.name);
					settle();
				}
			);
		});
		closeModal();
	};

	const hasAnything = projectFiles.length > 0 || projectFolders.length > 0;

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'task_modal.attach_project_file'})}</Modal.Title>
			<Modal.Content>
				{!hasAnything ? (
					<div data-testid={'attach-project-files-empty'} data-cy={'attach-project-files-empty'}>
						{formatMessage({id: 'task_modal.attach_project_file_empty'})}
					</div>
				) : (
					<FlexColumn gap={'m'}>
						<FlexRow gap={'s'} data-cy={'attach-project-files-breadcrumb'}>
							<BreadcrumbCrumb type={'button'} onClick={() => goToTrailIndex(-1)}>
								<Text>{formatMessage({id: 'project_files.all_files'})}</Text>
							</BreadcrumbCrumb>
							{trail.map((folder, index) => (
								<BreadcrumbCrumb key={folder.id} type={'button'} onClick={() => goToTrailIndex(index)}>
									<Text>{`/ ${folder.name}`}</Text>
								</BreadcrumbCrumb>
							))}
						</FlexRow>
						<SearchField
							isExpandable={false}
							value={searchValue}
							onChange={setSearchValue}
							placeholder={formatMessage({id: 'common.search'})}
						/>
						<Table
							tableRef={tableRef}
							data={rows}
							// MANDATORY. Table hands getRowId straight to TanStack, whose default derives a
							// row's id from its INDEX. `data` here is one level of the tree, replaced whenever
							// a folder is opened or the search term changes, while Table's selection map
							// survives that — so index ids make the selection re-bind to whatever row now sits
							// at each position, folders included. Keying on the file id is also what lets
							// enableRowSelection below hold folders out of a select-all.
							getRowId={(row: PickerRow) => row.id}
							onSelectionChange={onSelectionChange}
							// Receives the WRAPPED row, unlike onSelectionChange and the column's onClick.
							enableRowSelection={(row: {original: PickerRow}) => isRowSelectable(row.original)}
							width={'100%'}
						>
							<Table.TextColumn
								header={formatMessage({id: 'common.name'})}
								accessorKey={'name'}
								// Higher-order by design: onClick is asked for a handler per row and
								// must RETURN one, not call it. Returning openFolder(row) directly
								// would descend during render.
								onClick={(row: PickerRow) => (row.isFolder ? () => openFolder(row) : undefined)}
							/>
						</Table>
					</FlexColumn>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={attachSelected} disabled={attachBlocked}>
					{formatMessage({id: 'task_modal.attach_project_file_confirm'}, {count: selectedRows.length})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

// Both ids are Relay global ids and both arguments run them through fromGlobalId server-side:
// viewer.project takes `internalId`, but viewer.task has no internalId argument at all -- its
// equivalent is plain `id`.
export const attachProjectFilesModalQuery = graphql`
	query AttachProjectFilesModalQuery($projectId: ID, $taskId: ID) {
		viewer {
			actualPersonId
			component(name: "attach_project_files_modal")
			project(internalId: $projectId) {
				id
				files(first: 10000) @connection(key: "Project_files", filters: []) {
					edges {
						node {
							id
							name
							mimeType
							size
							folder {
								id
							}
						}
					}
				}
				# Drives the hierarchy. Folder.folder is the parent, so one flat list is enough to
				# reconstruct every level.
				folders(first: 10000) @connection(key: "Project_folders", filters: []) {
					edges {
						node {
							id
							name
							folder {
								id
							}
						}
					}
				}
			}
			task(id: $taskId) {
				id
				# Drives the already-attached rule. Nothing else produces it: showModal passes only
				# ids, and taskModalV3_viewer queries neither side. Without it the prop is undefined
				# and -- if defaulted to [] to dodge the crash -- every already-linked file becomes
				# selectable again, silently defeating the acceptance criterion.
				referencedFiles(first: 10000) @connection(key: "Task_referencedFiles", filters: []) {
					edges {
						node {
							id
						}
					}
				}
			}
		}
	}
`;

/**
 * The Task_referencedFiles key matches the one the attach mutation's RANGE_ADD appends to, so an
 * attach made from this modal updates its own view of what is already linked without a refetch.
 */
const AttachProjectFilesModal = ({
	taskId,
	projectId,
	closeModal,
}: {
	taskId: string;
	projectId: string;
	closeModal: () => void;
}) => (
	<ForecastQueryRenderer
		key="query-render-attachProjectFilesModal"
		query={attachProjectFilesModalQuery}
		modal={true}
		showLoader={false}
		variables={{projectId, taskId}}
		render={(relayProps: AttachProjectFilesRelayProps) => {
			const project = relayProps?.viewer?.project;
			const task = relayProps?.viewer?.task;
			if (!project || !task) return null;
			return (
				<AttachProjectFilesModalContent
					projectFiles={project.files.edges.map(edge => edge.node)}
					projectFolders={project.folders.edges.map(edge => edge.node)}
					taskFiles={task.referencedFiles.edges.map(edge => edge.node)}
					taskId={taskId}
					closeModal={closeModal}
				/>
			);
		}}
	/>
);

export default AttachProjectFilesModal;
